<template>
    <div>
        <data-table
            title="Organizations"
            delete-button-text="selected organizations"
            :headers="headers"
            :get-items-handler="getOrganizations"
            :get-delete-handler="deleteOrganization"
            :get-prepared-data-to-save="prepareDataToSave"
            :reset-filters-handler="resetFilters"
            has-filters
            :deletable="false"
        >
            <template v-slot:tableFilters>
                <organizations-filters
                    :filters="filters"
                ></organizations-filters>
            </template>
            <template v-slot:items="item">
                <td>
                    <template v-if="hasAllRoles(['OrganizationUpdate'])">
                        <router-link :to="{ name: 'editOrganizationDetails', params: { organizationUuid: item.props.item.id } }">
                            {{ item.props.item.name }}
                        </router-link>
                    </template>
                    <template v-else>
                        {{ item.props.item.name }}
                    </template>
                </td>
                <td>
                        {{ item.props.item.supportEmail}}
                </td>
                <td>
                        {{ item.props.item.supportPhoneNumber}}
                </td>
                <td>
                        {{ mapTimeZone(item.props.item.timeZone)}}
                </td>
                <td v-if="hasAllRoles(['OrganizationUpdate'])" class="text-center">
                    <router-link :to="{ name: 'editOrganizationDetails', params: { organizationUuid: item.props.item.id } }">
                        <sp-button design-type="table-button" icon="edit"></sp-button>
                    </router-link>
                </td>
            </template>

            <template v-slot:createButton v-if="hasAllRoles(['OrganizationAdd'])">
                <router-link :to="'/settings/organizations/create'" class="no-underline">
                    <sp-button sp-class="no-padding-right" icon="settings" text="Add organization"></sp-button>
                </router-link>
            </template>
        </data-table>
        <sp-loader v-if="isLoading"></sp-loader>
    </div>
</template>

<script>
import DataTable from '@/views/_components/DataTable'
import SpButton from '@/components/forms/Button'
import OrganizationsFilters from '@/views/Organizations/components/OrganizationsFilters'

import OrganizationApi from '@/api/OrganizationApi'
import { mapActions, mapGetters } from 'vuex'

import { searchEnumById } from '@/helpers/Utils'
import toast from '@/plugins/izitoast'
import SpLoader from '@/components/atoms/Loader'
import { setTableFiltersToLocalStorage, getTableFiltersFromLocalStorage } from '@/helpers/StorageHelper'

const organizationApi = new OrganizationApi()

export default {
    name: 'OrganizationsTable',
    components: {
        OrganizationsFilters,
        DataTable,
        SpButton,
        SpLoader
    },
    props: {
    },
    data () {
        return {
            headers: [
                {
                    text: 'Name',
                    sortable: true,
                    value: 'name'
                },
                {
                    text: 'Support Email',
                    sortable: true,
                    value: 'supportEmail'
                },
                {
                    text: 'Support Phone',
                    sortable: true,
                    value: 'supportPhoneNumber'
                },
                {
                    text: 'Time Zone',
                    sortable: false,
                    value: 'timeZone'
                },
                {
                    text: 'Edit',
                    sortable: false,
                    value: 'isEditable',
                    class: !this.hasAllRoles(['OrganizationUpdate']) ? 'd-none' : ''
                }
                // {
                //     text: 'Delete',
                //     sortable: false,
                //     value: 'isDeletable'
                // }
            ],
            totalResults: 0,
            isLoading: false,
            filters: {
                name: null,
                email: null,
                phone: null,
                users: null
            }
        }
    },
    computed: {
        ...mapGetters({
            getTimeZones: 'dictionaryModule/getTimeZones'
        }),
        ...mapGetters('authModule', [
            'getActiveOrganization',
            'getIsImpersonateActive',
        ]),
        ...mapGetters('managementTables', [
            'getOrganizationFilters'
        ])
    },
    mounted () {
        this.applySavedFilters()
    },
    methods: {
        ...mapActions('managementTables', [
            'setOrganizationFilters'
        ]),
        async getOrganizations (page, rowsPerPage, search, sortBy) {
            this.isLoading = true

            const response = await organizationApi.getOrganizations({
                name: this.filters.name,
                email: this.filters.email,
                phone: this.filters.phone,
                users: this.filters.users ? this.filters.users : null,
                page: page,
                perPage: rowsPerPage,
                orderBy: sortBy
            })

            this.totalResults = response.payload.totalResults

            if (!this.getIsImpersonateActive) {
                this.setOrganizationFilters(this.filters)
                setTableFiltersToLocalStorage('organizations', this.filters)
            }

            this.isLoading = false
            return {
                total: response.payload.totalResults,
                items: response.payload.items
            }
        },
        async applySavedFilters () {
            if (!this.getIsImpersonateActive) {
                const savedFilters = getTableFiltersFromLocalStorage('organizations')
                if (savedFilters) {
                    this.filters = { ...savedFilters }
                    return
                }
            } else {
                this.filters.name = null
                this.filters.email = null
                this.filters.phone = null
                this.filters.users = null
            }
            await this.getOrganization()
        },
        async deleteOrganization (organization) {
            const response = await organizationApi.deleteOrganizationById(organization.id)

            if (!response.isSuccess) {
                return
            }
            toast.success(`Organization ${organization.name} successfully deleted.`)
        },
        async prepareDataToSave (search) {
            const dataToSave = []
            const response = await organizationApi.getOrganizations({
                name: this.filters.name,
                email: this.filters.email,
                phone: this.filters.phone,
                users: this.filters.users ? this.filters.users : null
            })

            if (!response.isSuccess) {
                return
            }

            response.payload.items.forEach(item => {
                dataToSave.push({
                    name: item.name,
                    timeZone: this.mapTimeZone(item.timeZone),
                    supportEmail: item.supportEmail ? item.supportEmail : ' ',
                    supportPhoneNumber: item.supportPhoneNumber ? item.supportPhoneNumber : ' '
                })
            })
            return dataToSave
        },
        mapTimeZone (timeZoneId) {
            let timeZone = searchEnumById(this.getTimeZones, timeZoneId)

            return timeZone || ''
        },
        resetFilters () {
            this.filters = {
                name: null,
                email: null,
                phone: null
            }
        }
    }
}
</script>

<style scoped>
.margin-left {
    margin-left: 10px !important;
}
</style>
